package fr.frux.core.designsystem.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.selection.selectable
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.outlined.SignalCellularAlt
import androidx.compose.material.icons.outlined.SignalCellularAlt1Bar
import androidx.compose.material.icons.outlined.SignalCellularAlt2Bar
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.RadioButton
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.role
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp

private val TileShape = RoundedCornerShape(20.dp)
private val CardShape = RoundedCornerShape(16.dp)

/** Tuile d'espèce de la grille de l'assistant : disque de glyphe, nom (surligné si recherche), compte. */
@Composable
fun SpeciesTile(
    name: AnnotatedString,
    countLabel: String?,
    glyph: ImageVector,
    tint: Color,
    contentDescription: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Surface(
        onClick = onClick,
        modifier =
            modifier
                .heightIn(min = 112.dp)
                .semantics(mergeDescendants = true) {
                    this.contentDescription = contentDescription
                    role = Role.Button
                },
        shape = TileShape,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        shadowElevation = 1.dp,
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(start = 4.dp, top = 12.dp, end = 4.dp, bottom = 10.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            TreeGlyphDisc(glyph = glyph, tint = tint, size = 48.dp)
            Text(
                text = name,
                style = MaterialTheme.typography.labelLarge,
                color = MaterialTheme.colorScheme.onSurface,
                textAlign = TextAlign.Center,
                // Deux lignes réservées quel que soit le nom : les tuiles d'une grille gardent
                // toutes la même hauteur, qu'un nom tienne sur une ligne ou deux.
                minLines = 2,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
            )
            if (countLabel != null) {
                Text(
                    text = countLabel,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        }
    }
}

/** Carte de certitude de l'espèce : choix exclusif, [bars] de 1 à 3 pour le niveau de confiance. */
@Composable
fun CertaintyCard(
    title: String,
    description: String,
    bars: Int,
    selected: Boolean,
    onSelect: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val scheme = MaterialTheme.colorScheme
    val icon =
        when (bars) {
            1 -> Icons.Outlined.SignalCellularAlt1Bar
            2 -> Icons.Outlined.SignalCellularAlt2Bar
            else -> Icons.Outlined.SignalCellularAlt
        }
    Row(
        modifier =
            modifier
                .fillMaxWidth()
                .heightIn(min = 64.dp)
                .clip(CardShape)
                .background(if (selected) scheme.primaryContainer else scheme.surface)
                .border(
                    width = if (selected) 2.dp else 1.dp,
                    color = if (selected) scheme.primary else scheme.outline,
                    shape = CardShape,
                ).selectable(selected = selected, role = Role.RadioButton, onClick = onSelect)
                .padding(horizontal = 16.dp, vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        Icon(
            imageVector = icon,
            contentDescription = null,
            modifier = Modifier.size(24.dp),
            tint = scheme.primary,
        )
        Column(modifier = Modifier.weight(1f)) {
            Text(text = title, style = MaterialTheme.typography.titleMedium, color = scheme.onSurface)
            Text(
                text = description,
                style = MaterialTheme.typography.bodyMedium,
                color = scheme.onSurfaceVariant,
            )
        }
        // La ligne porte déjà la sémantique de sélection : le bouton radio n'est que visuel.
        RadioButton(selected = selected, onClick = null)
    }
}

private const val DISABLED_ALPHA = 0.38f
private val StatusIconDisc = 44.dp
private val StatusCheckDisc = 20.dp

/** Tuile d'état de l'arbre (cueilli, fruits mûrs...) : case à cocher grande, icône dans une pastille. */
@Composable
fun StatusTile(
    label: String,
    icon: ImageVector,
    iconContainer: Color,
    iconTint: Color,
    selected: Boolean,
    onToggle: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
) {
    val scheme = MaterialTheme.colorScheme
    // Overload Surface(checked, onCheckedChange) : l'ombre n'est plus rognée par un clip externe
    // et l'état coché est exposé dès la composition.
    Surface(
        checked = selected,
        onCheckedChange = { onToggle() },
        modifier =
            modifier
                .heightIn(min = 96.dp)
                .alpha(if (enabled) 1f else DISABLED_ALPHA)
                .semantics { role = Role.Checkbox },
        enabled = enabled,
        shape = CardShape,
        color = if (selected) scheme.primaryContainer else scheme.surface,
        border = BorderStroke(if (selected) 2.dp else 1.dp, if (selected) scheme.primary else scheme.outlineVariant),
        shadowElevation = if (selected) 0.dp else 1.dp,
    ) {
        Box {
            Column(
                modifier = Modifier.fillMaxWidth().padding(horizontal = 4.dp, vertical = 12.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                Box(
                    modifier = Modifier.size(StatusIconDisc).background(iconContainer, CircleShape),
                    contentAlignment = Alignment.Center,
                ) {
                    Icon(
                        imageVector = icon,
                        contentDescription = null,
                        modifier = Modifier.size(24.dp),
                        tint = iconTint,
                    )
                }
                Text(
                    text = label,
                    style = MaterialTheme.typography.labelLarge,
                    color = scheme.onSurface,
                    textAlign = TextAlign.Center,
                )
            }
            if (selected) {
                Box(
                    modifier =
                        Modifier
                            .align(Alignment.TopEnd)
                            .padding(6.dp)
                            .size(StatusCheckDisc)
                            .background(scheme.primary, CircleShape),
                    contentAlignment = Alignment.Center,
                ) {
                    Icon(
                        imageVector = Icons.Filled.Check,
                        contentDescription = null,
                        modifier = Modifier.size(14.dp),
                        tint = scheme.onPrimary,
                    )
                }
            }
        }
    }
}
